// English locale — the canonical source of truth and fallback for all other
// languages. Keys are grouped by website section. Lists are plain arrays so the
// UI can map over them directly.

export const en = {
  nav: {
    home: 'Home',
    about: 'About',
    features: 'Features',
    categories: 'Categories',
    howItWorks: 'How It Works',
    solutions: 'Solutions',
    contact: 'Contact',
    complaints: 'Complaints',
    fileComplaint: 'File a Complaint',
    trackComplaint: 'Track Complaint',
    signIn: 'Sign In',
    staffDashboard: 'Staff Dashboard',
    language: 'Language',
    changeLanguage: 'Change language',
    selectLanguage: 'Select language',
    skipToContent: 'Skip to main content',
  },

  hero: {
    badge: 'AI-Powered Civic Inspection',
    titleLine1: 'AI-Powered Civic',
    titleHighlight: 'Cleanliness Inspection',
    titleLine3: 'Platform',
    subtitle:
      'Capture geo-tagged civic evidence, analyze cleanliness with AI, submit reports to your municipality, assign cleaning tasks, track SLA, and verify resolution with before/after proof.',
    ctaFile: 'File a Complaint',
    ctaDemo: 'Book Municipal Demo',
    ctaTrack: 'Already filed? Track your complaint',
    trust1: 'GPS & timestamp proof',
    trust2: 'AI cleanliness scoring',
    trust3: 'SLA & audit reports',
    systemStatus: 'System Status',
    allActive: 'All Systems Active',
    degraded: 'Degraded',
    liveTracking: 'Live Tracking',
    fieldCapture: 'Field Capture',
    aiScore: 'AI Score',
    liveOverview: 'Live Overview',
    live: 'Live',
    unavailable: 'Unavailable',
    statsUnavailable: 'Live statistics are temporarily unavailable.',
    reports: 'Reports',
    clean: 'Clean',
    wards: 'Wards',
    inProgress: 'In Progress',
    hotspotMap: 'Hotspot map',
    slaAlert: 'SLA Alert',
    poweredBy: 'Powered by OpenAI GPT-4o Vision',
    stepSubmitted: 'Submitted',
    stepReview: 'Review',
    stepAssigned: 'Assigned',
    stepCleaning: 'Cleaning',
    stepVerified: 'Verified',
  },

  trustStrip: {
    municipalities: 'For Municipalities',
    sanitation: 'For Sanitation Teams',
    citizens: 'For Citizens',
    inspectors: 'For Inspectors',
  },

  problem: {
    badge: 'The Challenge',
    title: 'Manual Civic Inspection is Broken',
    subtitle: 'Traditional methods create blind spots, delays, and unverified claims.',
    items: [
      'Paper-based inspections are hard to verify',
      'Photos lack GPS / timestamp proof',
      'Delayed issue resolution',
      'No SLA tracking',
      'No evidence-based accountability',
      'Citizens cannot track progress',
    ],
    manualTitle: 'Manual Paper Inspection',
    manualForm: 'Handwritten form',
    manualFormSub: 'No location · No time · No proof',
    manualBads: ['Unverifiable claims', 'Lost in transit', 'No follow-up'],
    aiTitle: 'AI Mobile Inspection',
    aiScoreLabel: 'AI cleanliness score',
    aiGoods: ['GPS + timestamp', 'AI score & severity', 'Tracked to closure'],
  },

  solution: {
    badge: 'Our Solution',
    title: 'Technology-Driven Civic Safety',
    subtitle: 'Modernize sanitation inspection with AI-powered evidence collection and centralized oversight.',
    cards: [
      { title: 'Photo Evidence', sub: 'GPS & timestamped' },
      { title: 'AI Analysis', sub: 'Automated scoring' },
      { title: 'Live Tracking', sub: 'Real-time updates' },
      { title: 'Analytics', sub: 'Trend insights' },
    ],
    checklist: [
      'AI analyzes photos for instant cleanliness scoring',
      'GPS-verified evidence attached to every report',
      'Real-time dashboards for municipal authorities',
      'Public transparency through citizen complaints',
    ],
  },

  lifecycle: {
    badge: 'End-to-End Workflow',
    title: 'From Complaint to Resolution',
    subtitle: 'A single, traceable lifecycle — from the first photo to a verified, audit-ready closure.',
    cta: 'See the full workflow',
    steps: [
      { title: 'Capture Photo', desc: 'Officer or citizen photographs the civic issue on-site.' },
      { title: 'GPS & Timestamp Proof', desc: 'Every photo is stamped with location and time.' },
      { title: 'AI Cleanliness Analysis', desc: 'AI scores cleanliness, detects the issue and severity.' },
      { title: 'Submit to Municipality', desc: 'The report reaches the right ward and department.' },
      { title: 'Admin Review', desc: 'Officials verify the AI result and approve action.' },
      { title: 'Assign Cleaning Task', desc: 'Work is assigned to a sanitation team with a deadline.' },
      { title: 'Worker Uploads After Photo', desc: 'The team uploads proof once the work is done.' },
      { title: 'Before / After Verification', desc: 'Same-location before and after photos are compared.' },
      { title: 'SLA Closure', desc: 'The issue is closed within its SLA — or escalated.' },
      { title: 'PDF / Audit Report', desc: 'A tamper-evident audit report is generated.' },
    ],
  },

  ai: {
    badge: 'AI-Powered',
    title: 'How Swachh Bharat AI Works',
    subtitle:
      'Every photo runs through an AI pipeline that scores cleanliness, detects the issue, and recommends action — so decisions are consistent, fast and evidence-based.',
    capabilities: [
      { label: 'Checks image quality', desc: 'Rejects blurry, dark or unusable photos.' },
      { label: 'Detects the civic issue', desc: 'Identifies garbage, drains, debris and more.' },
      { label: 'Gives a cleanliness score', desc: '0–100 score with an A+ to F grade.' },
      { label: 'Grades severity & priority', desc: 'Flags high-severity issues for faster action.' },
      { label: 'Recommends action', desc: 'Suggests the likely department and next step.' },
      { label: 'Flags duplicate / invalid photos', desc: 'Catches repeats and manipulated images.' },
    ],
    trustBold: 'AI assists decision-making.',
    trustText: 'Final municipal action can be reviewed and overridden by authorized officials — humans stay in control.',
    resultTitle: 'AI Analysis Result',
    resultSub: 'Live example — garbage overflow',
    scoreLabel: 'Cleanliness score',
    grade: 'Grade C',
    rows: [
      { k: 'Detected issue', v: 'Garbage overflow' },
      { k: 'Severity', v: 'High' },
      { k: 'Recommended', v: 'Assign to sanitation team' },
      { k: 'Duplicate check', v: 'Passed' },
    ],
    analyzedBy: 'Analyzed by OpenAI GPT-4o Vision · reviewable by officials',
  },

  features: {
    badge: 'Platform Features',
    title: 'Everything You Need for Effective Oversight',
    subtitle: 'Comprehensive tools for municipal authorities, inspectors, and citizens',
    unavailable: 'Features are currently unavailable. Please try again later.',
    notConfigured: 'Platform features have not been configured yet. Please check back later.',
    viewAll: 'View All Features',
  },

  workflow: {
    badge: 'Workflow',
    title: 'Streamlined Inspection Workflow',
    subtitle: 'From field capture to resolution in a fully traceable process',
    step: 'Step',
    // Static fallback steps — used when the CMS/API has no workflow content so the
    // section never disappears, even if the backend is down.
    fallback: [
      { title: 'Capture', description: 'Field officer captures a geo-tagged photo.' },
      { title: 'Analyze', description: 'AI scores cleanliness and detects the issue.' },
      { title: 'Review', description: 'Officials review and approve the AI result.' },
      { title: 'Assign', description: 'A cleaning task is assigned with an SLA.' },
      { title: 'Verify', description: 'Before/after proof confirms the work.' },
      { title: 'Report', description: 'A tamper-evident audit report is generated.' },
    ],
  },

  beforeAfter: {
    badge: 'Verification',
    title: 'Proof-Based Cleaning Verification',
    subtitle:
      'Every closure is backed by matching before and after photos at the same location — not just a checkbox.',
    before: 'Before',
    after: 'After',
    beforeIssue: 'Garbage overflow',
    afterCleared: 'Cleared & cleaned',
    improvement: 'Improvement score',
    items: [
      { title: 'Before photo', desc: 'Captured by an officer or citizen when the issue is reported.' },
      { title: 'After photo', desc: 'Uploaded by the cleaning worker once the task is complete.' },
      { title: 'Same-location verification', desc: 'GPS confirms the after photo matches the reported site.' },
      { title: 'Improvement score', desc: 'AI compares both photos to measure the actual improvement.' },
      { title: 'Admin verification', desc: 'An official confirms the work before the issue is closed.' },
      { title: 'Reopen if not cleaned', desc: 'Sub-standard work is reopened and reassigned automatically.' },
      { title: 'PDF proof', desc: 'A before/after audit PDF is generated for the record.' },
    ],
  },

  commandCenter: {
    badge: 'Municipal Command Center',
    title: 'One Dashboard to Run the Whole City',
    subtitle: 'A real-time command center that turns thousands of reports into clear, prioritized action.',
    cta: 'View Dashboard Demo',
    features: [
      { title: 'Real-time command center', desc: 'One live view of every ward and report.' },
      { title: 'Pending review reports', desc: 'Incoming reports awaiting official review.' },
      { title: 'Action required reports', desc: 'Reports that need an immediate decision.' },
      { title: 'SLA overdue alerts', desc: 'Escalations when deadlines are missed.' },
      { title: 'Failed AI → manual review', desc: 'Low-confidence AI results routed to humans.' },
      { title: 'Ward performance', desc: 'Compare cleanliness across wards.' },
      { title: 'Category health score', desc: 'Track which issue types are trending.' },
      { title: 'Priority action queue', desc: 'The most urgent work, ranked first.' },
      { title: 'Map view', desc: 'Geospatial view of all live issues.' },
      { title: 'PDF / CSV export', desc: 'Export audit-ready reports on demand.' },
    ],
    mockTitle: 'Command Center',
    pending: 'Pending',
    action: 'Action',
    overdue: 'Overdue',
    resolved: 'Resolved',
    review: 'Review',
    wardPerformance: 'Ward performance',
    queue: ['Ward 12 · Garbage overflow', 'Ward 5 · Clogged drain', 'Ward 9 · Dirty road'],
  },

  mapIntel: {
    badge: 'Map Intelligence',
    title: 'Find Civic Hotspots Before They Become Bigger Problems',
    subtitle: 'Spot repeat offenders, clusters and overdue work on a live map — and act before complaints pile up.',
    cta: 'Explore Map Intelligence',
    features: [
      'Map-based report markers',
      'Ward-wise issue clusters',
      'Repeated dumping locations',
      'Overdue task markers',
      'High-severity filters',
      'Category heatmap',
      'Hotspot recommendations',
    ],
    legendHigh: 'High severity',
    legendOverdue: 'Overdue',
    legendResolved: 'Resolved',
  },

  stakeholder: {
    badge: 'Built for Every Stakeholder',
    title: 'One Platform, Every Role',
    subtitle: 'Purpose-built tools for administrators, officers, field teams, workers, and citizens.',
    cards: [
      { title: 'Municipality Admin', description: 'Run the whole city from one command center.', features: ['Real-time command center', 'SLA & escalation', 'Reports & analytics', 'PDF / CSV export'] },
      { title: 'Ward Officer', description: 'Review, assign and verify work in your ward.', features: ['Review reports', 'Assign cleaning tasks', 'Verify completion'] },
      { title: 'Field Officer', description: 'Capture evidence-backed inspections on the go.', features: ['Capture inspection', 'AI analysis', 'GPS proof', 'Submit report'] },
      { title: 'Sanitation Worker', description: 'Receive tasks and prove completed work.', features: ['Receive task', 'Upload after photo', 'Complete work'] },
      { title: 'Citizen', description: 'Report issues and track them to resolution.', features: ['File complaint', 'Track status', 'Give feedback'] },
    ],
  },

  security: {
    badge: 'Security & Governance',
    title: 'Built for Secure Civic Governance',
    subtitle: 'Enterprise-grade controls that municipal and government buyers expect.',
    items: [
      { title: 'Role-based access control', desc: 'Every role sees only what it should.' },
      { title: 'Audit logs', desc: 'Every action is recorded and traceable.' },
      { title: 'GPS & timestamp proof', desc: 'Evidence is location- and time-stamped.' },
      { title: 'Secure media upload', desc: 'Photos are stored and served securely.' },
      { title: 'Admin review', desc: 'Officials approve or reject every action.' },
      { title: 'AI override', desc: 'Humans can override any AI result.' },
      { title: 'PDF evidence', desc: 'Tamper-evident audit reports on demand.' },
      { title: 'Data privacy', desc: 'Citizen data is handled responsibly.' },
      { title: 'HTTPS security', desc: 'Encrypted end-to-end over TLS.' },
    ],
  },

  reporting: {
    badge: 'Reporting',
    title: 'Official Reports for Accountability',
    subtitle: 'Audit-ready documentation that stands up to scrutiny — generated automatically.',
    items: [
      'Inspection PDF',
      'Ward summary PDF',
      'Citizen complaint PDF',
      'Before/after PDF',
      'Monthly CSV export',
      'Scheduled email reports',
    ],
    cardTitle: 'Inspection Report',
    verified: 'Verified',
    before: 'Before',
    after: 'After',
    rows: [
      { k: 'Category', v: 'Garbage overflow' },
      { k: 'Location', v: '19.0760, 72.8770' },
      { k: 'Filed', v: '09 Jul 2026, 09:41' },
      { k: 'Closed', v: '09 Jul 2026, 15:20' },
      { k: 'SLA', v: 'Met (5h 39m)' },
    ],
    tamperEvident: 'Tamper-evident · audit trail attached',
  },

  stats: {
    labels: ['Civic Categories', 'AI Workflows', 'User Roles', 'Platform Interfaces'],
  },

  categories: {
    badge: 'Civic Categories',
    title: 'Comprehensive Coverage',
    subtitle: 'Monitor all types of civic infrastructure and sanitation categories',
    unavailable: 'Categories are currently unavailable.',
    retry: 'Retry',
    empty: 'No categories available',
    viewAll: 'View All Categories',
  },

  complaint: {
    badge: 'For Citizens',
    title: 'File or Track Your Complaint',
    subtitle: 'Help improve civic sanitation in your area — file a new complaint or check the status of an existing one.',
    fileTitle: 'File New Complaint',
    fileDesc: "Report a sanitation issue in your area. Your identity is kept confidential and you'll receive a tracking number to monitor progress.",
    fileSteps: ['Upload a photo of the issue', 'Choose a category', 'Capture your GPS location', 'Receive a tracking ID'],
    fileCta: 'File New Complaint',
    trackTitle: 'Track Complaint',
    trackDesc: 'Already filed a complaint? Track it by tracking ID or by your phone number with a one-time password.',
    trackSteps: ['Enter your tracking ID', 'Verify with OTP (by phone)', 'See your status timeline', 'View resolution proof'],
    trackCta: 'Track Complaint',
    timelineLabel: 'Your complaint status, end to end',
    statuses: ['Submitted', 'Under Review', 'Action Planned', 'In Progress', 'Resolved'],
  },

  faq: {
    badge: 'FAQ',
    title: 'Frequently Asked Questions',
    subtitle: 'Straight answers for citizens, municipalities and government buyers.',
    stillQuestions: 'Still have questions?',
    talkToTeam: 'Talk to our team',
    items: [
      { q: 'Who can use Swachh Bharat?', a: 'Municipalities, ward and field officers, sanitation workers, and everyday citizens. Each role gets a purpose-built experience — from the admin command center to the citizen complaint form.' },
      { q: 'Is this a government app?', a: 'Swachh Bharat is a civic technology platform built by a private company. It is designed for municipalities and civic authorities to deploy, and it makes no government affiliation claims.' },
      { q: 'How does AI analysis work?', a: 'Each photo is analyzed by AI (OpenAI GPT-4o Vision) to check image quality, detect the civic issue, assign a cleanliness score and severity, and recommend an action.' },
      { q: 'Can AI results be manually reviewed?', a: 'Yes. AI assists decision-making, but authorized officials can review, override, re-analyze, or reject any AI result before action is taken. Humans stay in control.' },
      { q: 'Can citizens track complaints?', a: 'Yes. Citizens receive a tracking ID and can follow a clear status timeline — Submitted, Under Review, Action Planned, In Progress, Resolved — and view resolution proof.' },
      { q: 'Does it work offline?', a: 'The field capture experience is mobile-first and tolerant of weak networks, queuing evidence and syncing once connectivity returns so field teams are never blocked.' },
      { q: 'Can the municipality assign cleaning tasks?', a: 'Yes. Ward officers can assign cleaning tasks to sanitation teams with deadlines, and track completion with before/after proof and SLA timers.' },
      { q: 'Does it support GPS proof?', a: 'Every photo is stamped with GPS coordinates and a timestamp, and after-photos are verified against the reported location to prevent fraud.' },
      { q: 'Can reports be exported as PDF?', a: 'Yes. Inspection reports, ward summaries, complaint records and before/after proofs can be exported as tamper-evident PDFs, plus monthly CSV and scheduled email reports.' },
      { q: 'Can it be customized for a city or municipality?', a: 'Yes. Categories, wards, SLA rules, escalation policies and branding can be configured per municipality. Book a demo to discuss your city\u2019s requirements.' },
    ],
  },

  cta: {
    badge: 'Get Started',
    title: 'Ready to Transform Civic Cleanliness Monitoring?',
    subtitle: 'Join municipalities in modernizing sanitation inspection with AI-powered evidence collection, SLA tracking, and centralized oversight.',
    primary: 'Book Municipal Demo',
    secondary: 'File a Complaint',
  },

  footer: {
    tagline: 'AI Civic Inspection Platform',
    description:
      'Enterprise-grade digital civic inspection platform designed for municipalities and civic authorities. AI-powered sanitation monitoring for cleaner, healthier communities.',
    india: 'India',
    product: 'Product',
    company: 'Company',
    complaints: 'Complaints',
    legal: 'Legal',
    links: {
      features: 'Features',
      categories: 'Categories',
      howItWorks: 'How It Works',
      solutions: 'Solutions',
      about: 'About Us',
      contact: 'Contact',
      signIn: 'Sign In',
      fileComplaint: 'File a Complaint',
      trackComplaint: 'Track Your Complaint',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    ctaTitle: 'Ready to get started?',
    ctaSubtitle: "Transform your municipality's civic inspection process today.",
    requestDemo: 'Request Demo',
    signIn: 'Sign In',
    rights: 'All rights reserved.',
    designedFor: 'Designed for municipalities and civic authorities',
  },
};

export type Locale = typeof en;
